import { DataResult, State } from '@progress/kendo-data-query';
import { ColumnMenu } from '../widgets/widget-subcomponents/TreeList.subcomponents';
import { Grid, GridColumn as Column, GridProps, GridNoRecords } from '@progress/kendo-react-grid';
import { PAGING_BUTTON_COUNT, PAGING_OPTIONS } from '../../constants';
import { WidgetID } from '../../models/widget.model';
import { GroupHeader } from './GroupHeader';
import { useGridWithExpansion } from './hooks/useGridWithExpansion';
import { useGridWithSort } from './hooks/useGridWithSort';
import { useGridWithGrouping } from './hooks/useGridWithGrouping';
import { useGridWithFiltering } from './hooks/useGridWithFiltering';
import { useGridWithPaging } from './hooks/useGridWithPaging';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { Feature } from '../../models/page-config.models';
import { useGridWithColumnReordering } from './hooks/useGridWithColumnReordering';
import { WidgetNoDataState } from '../widgets/WidgetNoDataState';
import { ComponentType } from 'react';
import { PagerProps } from '@progress/kendo-react-data-tools';
import { truncateString } from 'common/hooks/utils';
import { PopupPropsContext, PositionEvent } from '@progress/kendo-react-popup';
import { ColumnModel } from 'common/models/widget-config.model';

export interface DataGridProps {
  dataItemKey?: string;
  selectedField?: string;
  data: DataResult;
  columns: ColumnModel[];
  isSelectable?: boolean;
  widgetId: WidgetID;
  useColumnIdsForSort?: boolean;
  pager?: null | ComponentType<PagerProps>;
}

function DataGrid(props: DataGridProps & GridProps) {
  const {
    dataItemKey = 'id',
    selectedField = 'selected',
    columns: propColumns,
    isSelectable,
    total,
    useColumnIdsForSort,
    ...rest
  } = props;
  const columns = propColumns.map((column) => ({
    ...column,
    width: column.width,
  }));
  const widgetId = props.widgetId;
  const { onExpandChange, mapExpansionToData } = useGridWithExpansion();
  const { sort, onSortChange, isColumnSorted } = useGridWithSort(
    widgetId,
    columns,
    useColumnIdsForSort
  );
  const { grouping, onGroupChange, transformGridGroupedData } = useGridWithGrouping(widgetId);
  const { filter, onColumnFilter, isColumnFiltered } = useGridWithFiltering(widgetId);
  const { gridColumns, onColumnReorder } = useGridWithColumnReordering(widgetId, columns);
  const { paging, onPageChange } = useGridWithPaging(widgetId, total ?? 0);
  const { hasFeature } = useFeatureFlags();
  const transformedData = transformGridGroupedData(props.data);
  const expandedData = mapExpansionToData(transformedData);
  const isGroupable = props.groupable && hasFeature(Feature.DataGridGrouping);

  const columnProps = (field: string) => {
    const dataState: State = {
      sort: [],
    };
    if (sort) {
      dataState.sort = sort;
    }
    if (filter) {
      dataState.filter = filter;
    }

    let headerClass = isColumnSorted(field, dataState) ? 'k-sorted' : '';
    headerClass += isColumnFiltered(field, dataState) ? ' active' : '';
    return {
      headerClassName: headerClass,
    };
  };

  const safariPagingPopupRepositionPatch = (e: PositionEvent) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) return; // Skip positioning for non-Safari browsers
    const popupElement = e.target.element as HTMLElement;
    const anchorElement = e.target.props.anchor as HTMLElement;

    if (!popupElement || !anchorElement) return;

    // Get dimensions
    const popupRect = popupElement.getBoundingClientRect();
    const anchorRect = anchorElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Check if popup would overflow below
    const willOverflowBelow = anchorRect.bottom + popupRect.height > viewportHeight;

    if (willOverflowBelow) {
      // ✅ Position above the anchor
      const newTop = anchorRect.top - 336;
      const popupWidth = 96; // 96 per css if you change css change this, but based we go to bryntum this should not be much of problem

      popupElement.style.top = `${newTop}px`;
      popupElement.style.left = `${anchorRect.left - popupWidth}px`;
      popupElement.style.bottom = 'auto';
      popupElement.style.transform = 'translateY(-4px)';
      popupElement.style.zIndex = '10000';
    }
  };

  return (
    <div className="data-grid-wrapper">
      <PopupPropsContext.Provider
        value={(props) => ({
          ...props,
          animate: true,
          popupAlign: { horizontal: 'center', vertical: 'bottom' },
          positionMode: 'absolute',
          appendTo: document.body,
          anchorAlign: { horizontal: 'center', vertical: 'top' },
          onPosition: safariPagingPopupRepositionPatch,
        })}
      >
        <Grid
          key={gridColumns.map((c) => c.field).join(',')}
          resizable={false}
          // data related props
          reorderable={true}
          dataItemKey={dataItemKey}
          selectedField={selectedField}
          expandField="expanded"
          skip={paging?.skip}
          take={paging?.take ?? PAGING_OPTIONS[0]}
          {...rest}
          onExpandChange={onExpandChange}
          data={expandedData}
          pageable={
            total && total > 0
              ? {
                  responsive: false,
                  info: true,
                  buttonCount: PAGING_BUTTON_COUNT,
                  pageSizes: PAGING_OPTIONS,
                }
              : false
          }
          total={total}
          onPageChange={onPageChange}
          style={{ overflow: 'auto', width: '100%' }}
          onFilterChange={onColumnFilter}
          onSortChange={onSortChange}
          sort={sort}
          groupable={isGroupable}
          group={isGroupable ? grouping : []}
          onGroupChange={onGroupChange}
          cells={{
            group: {
              groupHeader: GroupHeader,
            },
          }}
          filter={filter}
          onColumnReorder={onColumnReorder}
        >
          <GridNoRecords>
            <WidgetNoDataState />
          </GridNoRecords>
          {isSelectable && (
            <Column
              sortable={false}
              filterable={false}
              field={selectedField}
              className="selected-field"
            />
          )}
          {gridColumns.map((col) => {
            return (
              <Column
                {...columnProps(col.field as string)}
                columnMenu={(props: any) => <ColumnMenu {...props} />}
                key={col.field}
                {...col}
                title={truncateString(col.title, 25)}
                width={col.width}
              />
            );
          })}
        </Grid>
      </PopupPropsContext.Provider>
    </div>
  );
}

export default DataGrid;
